import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams, useNavigate} from "react-router-dom";
import { authFetch } from './auth';

const smoothData = (data, windowSize = 5) => {
  return data.map((point, index, array) => {
    let start = Math.max(0, index - Math.floor(windowSize / 2));
    let end = Math.min(array.length - 1, index + Math.floor(windowSize / 2));
    let values = array.slice(start, end + 1).map((p) => p.speed);
    let avgSpeed = Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
    return {
      ...point,
      speed: Math.ceil(avgSpeed),
      altitude: Math.ceil(point.altitude),
      time: Math.round(point.time),
    };
  });
};
const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};
// Форматируем время из секунд в "X мин Y сек"
const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return "-";
  if (seconds < 60) return `${seconds} сек`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs === 0 ? `${mins} мин` : `${mins} мин ${secs} сек`;
};

// Выводим поля, если данных нет — "-"
const displayField = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const App = () => {
  const { id } = useParams();
  const [jump, setJump] = useState({});
  const [kmhData, setKmhData] = useState([]);
  const [msData, setMsData] = useState([]);
  const [speedUnit, setSpeedUnit] = useState("kmh");
    const navigate = useNavigate();
  const [maxSpeedKmH, setMaxSpeedKmH] = useState(0);
  const [maxSpeedMS, setMaxSpeedMS] = useState(0);
    const [displayData, setDisplayData] = useState([]);
  

  useEffect(() => {
    authFetch(`/api/jumpdata/public/${id}`)
      .then((response) => response.json())
      .then((data) => {
              setJump(data);

      const altDataRaw = Array.isArray(data.altimeterData) ? data.altimeterData : [];

      // 🔥 обрезаем на первом altitude === 0
      let cutIndex = altDataRaw.findIndex(point => point.altitude === 0);

      // если ноль не найден — берем весь массив
      if (cutIndex === -1) {
        cutIndex = altDataRaw.length;
      }

      const altData = altDataRaw.slice(0, cutIndex);

      // Обрабатываем данные для графика
      if (altData.length > 0) {
        const processedData = altData.map((point) => ({
          time: Math.round(point.timeOfJump),
          altitude: Math.ceil(point.altitude),
          speed: Math.ceil(point.speed),
          duration: Math.round(point.duration),
        }));

        const smoothedData = smoothData(processedData);

        setKmhData(smoothedData);
        setMsData(smoothedData.map(p => ({ ...p, speed: Math.round(p.speed / 3.6) })));

        const maxS = Math.ceil(Math.max(...altData.map(d => d.speed)));
        setMaxSpeedKmH(maxS);
        setMaxSpeedMS(Math.round(maxS / 3.6));
      } else {
        setKmhData([]);
        setMsData([]);
        setMaxSpeedKmH(0);
        setMaxSpeedMS(0);
      }
    });
  }, [id]);

  const handleUnitChange = (newUnit) => {
    setSpeedUnit(newUnit);
    setDisplayData(newUnit === "kmh" ? maxSpeedKmH : maxSpeedMS);
  };

  // Выбираем массив для графика в зависимости от единиц
  const chartData = speedUnit === "kmh" ? kmhData : msData;
  const chartMaxSpeed = speedUnit === "kmh" ? maxSpeedKmH : maxSpeedMS;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "20px",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <div
        style={{
          width: "95%",
          maxWidth: "1200px",
          backgroundColor: "rgba(17, 17, 17, 0.75)",
          borderRadius: "20px",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
          padding: "30px",
          position: "relative",
        }}
      >
      

        <h2 style={{ textAlign: "center", marginBottom: "20px", color: "#fff" }}>
          Подробности о прыжке
        </h2>

        {/* График или плашка при отсутствии данных */}
        {chartData.length === 0 ? (
          <div
            style={{
              margin: "20px auto",
              background: "rgba(17,17,17,0.7)",
              padding: "20px",
              borderRadius: "12px",
              border: "1px solid rgba(255,255,255,0.2)",
              width: "100%",
              maxWidth: "900px",
              color: "#fff",
              textAlign: "center",
              overflowWrap: "break-word",
              boxSizing: "border-box",
            }}
          >
            Данный прыжок добавлен ручным способом, показания прибора для построения графика прыжка отсутствуют
          </div>
        ) : (
          <>
            <ResponsiveContainer width="95%" height={400}>
              <LineChart data={chartData}>
                <XAxis
                  dataKey="time"
                  label={{ value: "Время (сек)", position: "insideBottom", dy: 10 }}
                  stroke="#fff"
                />
                <YAxis
                  yAxisId="left"
                  label={{ value: "Высота (м)", angle: -90, position: "insideLeft" }}
                  stroke="#fff"
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  label={{ value: `Скорость (${speedUnit === "kmh" ? "км/ч" : "м/с"})`, angle: -90, position: "insideRight" }}
                  domain={[0, chartMaxSpeed]}
                  stroke="#fff"
                />
                <Tooltip
                  contentStyle={{ backgroundColor: "#222", color: "#fff", border: "none" }}
                  itemStyle={{ color: "#fff" }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend wrapperStyle={{ marginTop: 30, color: "#fff" }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="altitude"
                  stroke="#5372c7"
                  strokeWidth={4}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="natural"
                  dataKey="speed"
                  stroke="#DC2626"
                  strokeWidth={3}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>

            <div style={{ marginTop: "15px", display: "flex", justifyContent: "center", gap: "10px" }}>
              <button
                onClick={() => handleUnitChange("kmh")}
                style={{
                  padding: "6px 12px", borderRadius: "8px",
                  border: speedUnit === "kmh" ? "2px solid #FFD700" : "none",
                  background: "#333", color: "#fff", cursor: "pointer"
                }}>км/ч</button>
              <button
                onClick={() => handleUnitChange("ms")}
                style={{
                  padding: "6px 12px", borderRadius: "8px",
                  border: speedUnit === "ms" ? "2px solid #FFD700" : "none",
                  background: "#333", color: "#fff", cursor: "pointer"
                }}>м/с</button>
            </div>
          </>
        )}

        {/* Информация о прыжке - логические блоки */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            marginTop: "20px",
            gap: "15px",
          }}
        >
          {/* Локация */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Дропзона:</strong> {displayField(jump.dropzone)}</p>
            <p><strong>Страна:</strong> {displayField(jump.country)}</p>
            <p><strong>Город:</strong> {displayField(jump.city)}</p>
          </div>

          {/* Прыжок и аппарат */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Номер прыжка:</strong> {displayField(jump.jumpNumber)}</p>
            <p><strong>Дата и время:</strong> {formatDateTime(jump.dateTime)}</p>
            <p><strong>Тип прыжка:</strong> {displayField(jump.jumpType)}</p>
            <p><strong>Тип летательного аппарата:</strong> {displayField(jump.aircraftType)}</p>
            <p><strong>Тип купола:</strong> {displayField(jump.canopyType)}</p>
          </div>

          {/* Высоты и скорости */}
          <div style={{ flex: "1 1 250px", background: "rgba(17,17,17,0.6)", padding: "15px", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.2)", color: "#fff" }}>
            <p><strong>Высота отделения:</strong> {displayField(jump.exitAltitude)}</p>
            <p><strong>Высота раскрытия:</strong> {displayField(jump.deployAltitude)}</p>
            <p><strong>Максимальная скорость:</strong> {chartData.length === 0 ? "-" : (speedUnit === "kmh" ? maxSpeedKmH + " км/ч" : maxSpeedMS + " м/с")}</p>
            <p><strong>Время свободного падения:</strong> {formatTime(jump.freefallTime)}</p>
            <p><strong>Время под куполом:</strong> {formatTime(jump.canopyPilotTime)}</p>
            <p><strong>Общая длительность прыжка:</strong> {formatTime(jump.jumpDuration)}</p>
          </div>
        </div>

        {/* Комментарий */}
        <div style={{
          margin: "20px auto",
          background: "rgba(17,17,17,0.7)",
          padding: "20px",
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.2)",
          width: "100%",
          maxWidth: "900px",
          color: "#fff",
          textAlign: "center",
          overflowWrap: "break-word",
          boxSizing: "border-box",
        }}>
          <h3 style={{ color: "#FFD700", marginBottom: "10px" }}>Описание прыжка</h3>
          <p style={{ whiteSpace: "pre-wrap", textAlign: "center", margin: 0 }}>
            {displayField(jump.comment)}
          </p>
        </div>

        {/* Кнопки навигации */}
        <div
          style={{
            marginTop: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            alignItems: "center",
          }}
        >
                               <button style={portalBtnStyle} onClick={() => navigate("/")}>
          Перейти на портал
        </button>
        </div>
      </div>

    </div>
  );
};

export default App;
const portalBtnStyle = {
  padding: "14px 28px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #10B981, #059669)",
  color: "white",
  fontSize: "16px",
  fontWeight: "600",
  cursor: "pointer",
};